import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Accordion,
  Modal,
  Form,
} from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import axios from "axios";
import apiConfig from "../credentials/apiConfig"; // adjust path if needed
import IconTicket from "../../images/icon-ticket.png";
import { FaTicketAlt, FaQuestion, FaAngleDown } from "react-icons/fa";
import "./index.scss";
import DownloadSampleFile from "../../components/DownloadSampleFile";

const ticketSchema = Yup.object().shape({
  subject: Yup.string().required("Subject is required"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters"),
});

const HelpAndSupport = () => {
  const [showModal, setShowModal] = useState(false);

  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);

  const handleSubmitTicket = async (values, { resetForm }) => {
    const userId = localStorage.getItem("userId");
    try {
      const authToken = localStorage.getItem("authToken");
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/createSupportTicket`,
        {
          title: values.subject,
          description: values.description,
          userId,
        },
        {
          headers: {
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Ticket submitted successfully!");
        handleClose();
        resetForm();
      } else {
        toast.error(response.data.message || "Something went wrong!");
      }
    } catch (error) {
      console.error("Error submitting ticket:", error);
      toast.error("Failed to submit ticket. Please try again.");
    }
  };

  return (
    <div className="help-support-page">
      <p className="text-light mb-4 bg-white w-100 shadow-sm p-3">
        Find answers, tutorials, and support to help you get the most out of
        AuditPro.ai
      </p>

      <Container fluid className="mt-2 mb-5 ">
        <Row className="mb-2">
          <Col md={12}>
            <h3 className=" fw-bold" style={{ fontSize: "20px" }}>
              Contact Support
            </h3>
          </Col>
        </Row>

        <Row className="mb-5">
          <Col md={12}>
            <Card className="border-0 shadow-sm">
              <Card.Body className="new-ticket-block d-flex align-items-center">
                <div className="me-3">
                  <img
                    src={IconTicket}
                    alt="auto"
                    className="me-2 my-auto"
                    width={35}
                    height={35}
                  />
                </div>
                <div className="flex-grow-1">
                  <h5
                    className="text-primary fw-bold mb-0"
                    style={{ fontSize: "16px" }}
                  >
                    Create a Ticket
                  </h5>
                  <p className="mb-0 text-light">
                    Provide feedback, request help, or report an issue
                  </p>
                </div>
                <div className="my-auto">
                  <Button
                    variant="primary"
                    onClick={handleShow}
                    className="px-4"
                  >
                    New Ticket
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="mb-2">
          <Col md={12}>
            <h3 className=" fw-bold" style={{ fontSize: "20px" }}>
              Frequently Asked Questions
            </h3>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Accordion defaultActiveKey="0" className="mb-5">
              <Accordion.Item eventKey="0">
                <Accordion.Header className="text-primary fw-bold bg-white">
                  Who are users of AuditPro.ai?
                </Accordion.Header>
                <Accordion.Body className="text-light">
                  Accounting firms, companies, and investment firms of all sizes
                  use AuditPro.ai. Auditors use the platform to help with risk
                  assessment and analysis of public and private companies in
                  every industry and non-profit organizations. CFOs and analysts
                  use AuditPro.ai to help optimize performance with research and
                  analysis tailored to their company. Venture capital and
                  private equity firms use the platform to expedite due
                  diligence and monitor portfolio companies.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  Is training available for our team?
                </Accordion.Header>
                <Accordion.Body>
                  Visit AuditPro.ai/Demo to register for free upcoming training.
                  You can also complete the form on AuditPro.ai/Demo to schedule
                  a time that works best for you.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  Where can I find information about AuditPro.ai's security?
                </Accordion.Header>
                <Accordion.Body>
                  Visit AuditPro.ai/Security to learn more about our data
                  privacy, authentication, and operations.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  Is there a required format for trial balances?
                </Accordion.Header>
                <Accordion.Body>
                  There is a required format, and an example file can be found
                  here:{" "}
                  <DownloadSampleFile
                    fileName="Trial_Balance_Template.xlsx"
                    displayText="Sample Current Year Trial Balance"
                  />
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="4">
                <Accordion.Header>
                  I have a question or idea to share with AuditPro.ai. Who
                  should I contact?
                </Accordion.Header>
                <Accordion.Body>
                  Within the Help and Support tab, click the New Ticket button
                  and complete the form, and a member of our team will contact
                  you. You can also email{" "}
                  <a href="mailto:Support@AuditPro.ai" className="text-primary">
                    Support@AuditPro.ai
                  </a>
                  .
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="5">
                <Accordion.Header>
                  Can I integrate my company's or client's accounting system
                  with AuditPro.ai?
                </Accordion.Header>
                <Accordion.Body>
                  Integration with a range of accounting systems will be
                  available soon.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="6">
                <Accordion.Header>
                  Can other people in my organization who are users of
                  AuditPro.ai see the clients I create?
                </Accordion.Header>
                <Accordion.Body>
                  Only you can see the clients you create and sharing clients
                  with other users is not allowed.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="7">
                <Accordion.Header>
                  What AI technologies does AuditPro.ai use?
                </Accordion.Header>
                <Accordion.Body>
                  Contact{" "}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={handleShow}
                    onKeyPress={(e) => e.key === "Enter" && handleShow()}
                    className="text-primary text-decoration-underline"
                    style={{ cursor: "pointer" }}
                  >
                    Support@AuditPro.ai
                  </span>{" "}
                  to learn more about our proprietary technology.
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="8">
                <Accordion.Header>Can I change my password?</Accordion.Header>
                <Accordion.Body>
                  Login, go to Settings, scroll to Security, and click the
                  Change password.
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Col>
        </Row>
      </Container>

      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        className="credential-page"
      >
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">New Ticket</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{ subject: "", description: "" }}
          validationSchema={ticketSchema}
          onSubmit={handleSubmitTicket}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Subject <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    value={values.subject}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.subject && errors.subject}
                    maxLength={150}
                    className="p-3"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.subject}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    rows={5}
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.description && errors.description}
                    className="p-3"
                    maxLength={500}
                    style={{ backgroundColor: "#E7EFF8" }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.description}
                  </Form.Control.Feedback>
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="outline-primary"
                  onClick={handleClose}
                  className="px-3"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  className="px-3"
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default HelpAndSupport;